import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../config/firebaseConfig';
import { doc, getDoc, updateDoc, setDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupIcon from '@mui/icons-material/Group';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import './ExperienceCard.css';

const ExperienceCard = ({ experience }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [reviewStats, setReviewStats] = useState({
    totalNumberOfReviews: 0,
    averageRating: 0
  });
  const { user } = useAuth();

  useEffect(() => {
    const fetchReviewStats = async () => {
      try {
        const reviewStatsRef = doc(db, 'experienceReviews', experience.id);
        const reviewStatsDoc = await getDoc(reviewStatsRef);
        
        if (reviewStatsDoc.exists()) {
          const data = reviewStatsDoc.data();
          setReviewStats({
            totalNumberOfReviews: data.totalNumberOfReviews || 0,
            averageRating: data.averageRating ? Number(data.averageRating.toFixed(1)) : 0
          });
        }
      } catch (error) {
        console.error('Error fetching review stats:', error);
      }
    };

    fetchReviewStats();
  }, [experience.id]);

  useEffect(() => {
    const checkIfFavorite = async () => {
      if (!user) return;
      
      try {
        const favoritesRef = doc(db, 'experienceFavorites', user.uid);
        const favoritesDoc = await getDoc(favoritesRef);
        
        if (favoritesDoc.exists()) {
          const favorites = favoritesDoc.data().experienceIds || [];
          setIsFavorite(favorites.includes(experience.id));
        }
      } catch (error) {
        console.error('Error checking favorite status:', error);
      }
    };

    checkIfFavorite();
  }, [user, experience.id]);

  const toggleFavorite = async (e) => {
    e.preventDefault();
    if (!user) return;

    try {
      const favoritesRef = doc(db, 'experienceFavorites', user.uid);
      const favoritesDoc = await getDoc(favoritesRef);

      if (!favoritesDoc.exists()) {
        await setDoc(favoritesRef, { experienceIds: [experience.id] });
        setIsFavorite(true);
      } else {
        await updateDoc(favoritesRef, {
          experienceIds: isFavorite
            ? arrayRemove(experience.id)
            : arrayUnion(experience.id)
        });
        setIsFavorite(!isFavorite);
      }
    } catch (error) {
      console.error('Error updating favorites:', error);
    }
  };

  const nextImage = (e) => {
    e.preventDefault();
    if (experience.images && experience.images.length > 1) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === experience.images.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  const prevImage = (e) => {
    e.preventDefault();
    if (experience.images && experience.images.length > 1) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === 0 ? experience.images.length - 1 : prevIndex - 1
      );
    }
  };

  const getDisplayPrice = () => {
    if (experience.options && experience.options.length > 0) {
      // Get group experience prices
      const groupPrices = experience.options
        .filter(opt => opt.type === 'Group Experience' && opt.pricePerPerson)
        .map(opt => parseFloat(opt.pricePerPerson));
      
      // Get private experience prices
      const privatePrices = experience.options
        .filter(opt => opt.type === 'Private Experience' && opt.privateMinPrice)
        .map(opt => parseFloat(opt.privateMinPrice));
      
      const minGroupPrice = groupPrices.length > 0 ? Math.min(...groupPrices) : Infinity;
      const minPrivatePrice = privatePrices.length > 0 ? Math.min(...privatePrices) : Infinity;
      
      // Return the lower price and whether it's private
      if (minGroupPrice <= minPrivatePrice && minGroupPrice !== Infinity) {
        return { price: minGroupPrice, isPrivate: false };
      } else if (minPrivatePrice !== Infinity) {
        return { price: minPrivatePrice, isPrivate: true };
      }
    }
    return { price: experience.pricePerPerson || 0, isPrivate: false };
  };

  const formatDuration = (hours, minutes) => {
    const h = hours || 0;
    const m = minutes || 0;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    if (m > 0) return `${m}m`;
    return '';
  };

  const getDisplayDuration = () => {
    if (experience.options && experience.options.length > 0) {
      // Calculate total duration in minutes for comparison
      const totalMinutes = experience.options.map(opt => {
        const h = opt.duration || 0;
        const m = opt.durationMinutes || 0;
        return h * 60 + m;
      }).filter(t => t > 0);
      
      if (totalMinutes.length > 0) {
        const minTotal = Math.min(...totalMinutes);
        const maxTotal = Math.max(...totalMinutes);
        
        const formatTotalMinutes = (total) => {
          const h = Math.floor(total / 60);
          const m = total % 60;
          return formatDuration(h, m);
        };
        
        if (minTotal === maxTotal) {
          return formatTotalMinutes(minTotal);
        }
        return `${formatTotalMinutes(minTotal)}-${formatTotalMinutes(maxTotal)}`;
      }
    }
    return experience.duration ? formatDuration(experience.duration, experience.durationMinutes) : '';
  };

  const getMaxGuests = () => {
    if (experience.options && experience.options.length > 0) {
      const maxGuestValues = experience.options
        .map(opt => opt.maxGuests)
        .filter(Boolean);
      if (maxGuestValues.length > 0) {
        return Math.max(...maxGuestValues);
      }
    }
    return experience.maxGuests || 10;
  };

  return (
    <Link to={`/experience/${experience.id}`} className="experience-card">
      <div className="experience-card-image-container">
        <div className="experience-badge">🎯 Experience</div>
        <div className="experience-category-badge">{experience.category}</div>
        {user && (
          <button 
            className={`favorite-button ${isFavorite ? 'favorited' : ''}`}
            onClick={toggleFavorite}
          >
            {isFavorite ? <FavoriteIcon sx={{ color: '#ff1744' }} /> : <FavoriteBorderIcon sx={{ color: '#757575' }} />}
          </button>
        )}
        {experience.images && experience.images.length > 0 ? (
          <img 
            src={experience.images[currentImageIndex]} 
            alt={experience.name} 
            className="experience-image" 
            loading="lazy"
          />
        ) : (
          <div className="experience-image-placeholder">No Image</div>
        )}
        {experience.images && experience.images.length > 1 && (
          <>
            <button className="nav-button prev" onClick={prevImage}>❮</button>
            <button className="nav-button next" onClick={nextImage}>❯</button>
            <div className="image-dots">
              {experience.images.map((_, index) => (
                <span 
                  key={index} 
                  className={`dot ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentImageIndex(index);
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>
      <div className="experience-info">
        <div className="experience-header">
          <h3 className='experience-title'>{experience.name}</h3>
          {reviewStats.totalNumberOfReviews > 0 ? (
            <div className="experience-rating">
              <span className="star">★</span>
              <span className="rating-score">{reviewStats.averageRating}</span>
              <span className="review-count">({reviewStats.totalNumberOfReviews})</span>
            </div>
          ) : (
            <span className="experience-new-badge">New</span>
          )}
        </div>
        <p className="experience-description">{experience.shortDescription}</p>
        <div className="experience-details">
          {getDisplayDuration() && (
            <span className="experience-card-detail-item">
              <AccessTimeIcon sx={{ fontSize: 12 }} />
              {getDisplayDuration()}
            </span>
          )}
          <span className="experience-card-detail-item">
            <GroupIcon sx={{ fontSize: 12 }} />
            Up to {getMaxGuests()} guests
          </span>
        </div>
        <div className="experience-location">
          <LocationOnIcon sx={{ fontSize: 12 }} />
          <span>{experience.cityRegion || experience.location}</span>
        </div>
        {(() => {
          const priceInfo = getDisplayPrice();
          return (
            <div className="experience-price">
              <span className="price-label">From</span>
              <span className="price">€{priceInfo.price.toLocaleString('en-US')}</span>
              <span className="price-period">{priceInfo.isPrivate ? '/group' : '/person'}</span>
            </div>
          );
        })()}
      </div>
    </Link>
  );
};

export default ExperienceCard;
